import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Calendar, Sparkles, Loader2, RefreshCw, MessageSquare, FileText, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { aiService } from "@/services/aiService";
import { getCurrentUserId } from "@/lib/supabase";
import { operatingLoopService } from "@/services/operatingLoopService";
import { creditsService } from "@/services/creditsService";
import {
  PERIODS,
  fetchActivity,
  buildDigestPrompt,
  type PeriodKey,
  type ActivityItem,
} from "@/lib/dailyDigest";

/**
 * Daily Digest — an activity journal / fallback reference: "what was I working
 * on" over an adjustable window (default 24h). Reads your recent chats + docs
 * (RLS-scoped) and, on demand, summarizes them into surface-level bullets via
 * the host AI proxy. Client-side; no backend endpoint.
 */
export default function DailyDigest() {
  const { toast } = useToast();
  const [period, setPeriod] = useState<PeriodKey>("24h");
  const [highlights, setHighlights] = useState<string>("");

  const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? "Last 24 hours";

  const activityQuery = useQuery<ActivityItem[]>({
    queryKey: ["daily-digest", "activity", period],
    queryFn: () => fetchActivity(period, Date.now()),
  });
  const activity = activityQuery.data ?? [];

  const counts = useMemo(() => {
    const c = { thread: 0, source: 0, knowledge: 0 };
    for (const a of activity) c[a.kind]++;
    return c;
  }, [activity]);

  const generate = useMutation({
    mutationFn: async () => {
      const gate = await creditsService.assertPositiveBalanceForAi();
      if (!gate.ok) throw new Error("Not enough AI credits to generate a digest.");
      const prompt = buildDigestPrompt(activity, periodLabel);
      setHighlights("");
      const gated = await operatingLoopService.answerWithLoop({
        userId: await getCurrentUserId(),
        question: prompt,
        pickedModel: "openai/gpt-4o-mini",
        autoSelectBest: true,
        app: "daily_digest",
        run: async ({ model, briefPrompt }) => {
          let out = "";
          for await (const chunk of aiService.streamChat({
            model,
            messages: [
              ...(briefPrompt ? [{ role: "system" as const, content: briefPrompt }] : []),
              { role: "user" as const, content: prompt },
            ],
            stream: true,
            maxTokens: 600,
            temperature: 0.4,
            app: "daily_digest",
          })) {
            if (chunk.type === "error") throw new Error(chunk.error || "Digest generation failed");
            if (chunk.type === "delta" && chunk.content) {
              out += chunk.content;
              setHighlights(out);
            }
          }
          return { text: out };
        },
      });
      setHighlights(gated.text);
      return gated.text;
    },
    onError: (e: Error) => toast({ title: "Couldn't generate digest", description: e.message, variant: "destructive" }),
  });

  const iconFor = (k: ActivityItem["kind"]) =>
    k === "thread" ? <MessageSquare className="h-3.5 w-3.5" /> : k === "source" ? <FileText className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" /> Daily Digest
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            A quick journal of what you were working on — chats and documents you touched. Adjustable window.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map((p) => (
            <Button key={p.key} size="sm" variant={p.key === period ? "default" : "outline"} onClick={() => setPeriod(p.key)}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Highlights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" /> Highlights — {periodLabel.toLowerCase()}
          </CardTitle>
          <CardDescription>Surface-level bullets generated from your activity below.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => generate.mutate()} disabled={generate.isPending || activityQuery.isLoading}>
            {generate.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
            ) : highlights ? (
              <><RefreshCw className="h-4 w-4 mr-2" /> Regenerate</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate highlights</>
            )}
          </Button>
          {highlights ? (
            <div className="text-sm whitespace-pre-wrap leading-relaxed border rounded-md p-3 bg-muted/30">{highlights}</div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {activity.length ? "Click generate for a quick recap." : "No activity in this window yet."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Raw activity — the fallback reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Activity
            <Badge variant="secondary">{activity.length}</Badge>
          </CardTitle>
          <CardDescription className="flex gap-3">
            <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {counts.thread} chats</span>
            <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {counts.source} docs</span>
            <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {counts.knowledge} KB</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {activityQuery.isLoading ? (
            <>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-6 w-5/6" />
            </>
          ) : activity.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing touched in {periodLabel.toLowerCase()}.</p>
          ) : (
            activity.map((a) => (
              <div key={`${a.kind}-${a.id}`} className="flex items-center gap-2 text-sm py-1">
                <span className="text-muted-foreground">{iconFor(a.kind)}</span>
                <span className="truncate flex-1">{a.title || "(untitled)"}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {a.updatedAt ? new Date(a.updatedAt).toLocaleString() : ""}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
